import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRightIcon,
  BanknotesIcon,
  BookOpenIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  QuestionMarkCircleIcon,
  TicketIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { CURRENCY_RATES, DLR_EXTRA_PRICES, convertPrice, formatAmount } from '../utils/currency';
import { DLR_GAMES } from '../utils/dlrGames';
import MarketingNav from '../components/Marketing/MarketingNav';
import {
  AppPage, BTN_PRIMARY, BTN_SECONDARY, CARD, CARD_FEATURED, Eyebrow, HeroPanel, SectionTitle, Segmented,
} from '../components/UI/AppUI';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.air-brief.com';


const PROGRAMME_BLURBS = {
  ATPL: 'Every ATPL theory subject with real exam questions, practice by chapter and timed mock exams.',
  DLR: 'DLR and airline screening preparation: theory questions plus the assessment games airlines use.',
  bundle: 'ATPL theory and DLR preparation together in one plan.',
};

// Helper function to get full image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  return `${API_URL}${imagePath}`;
};

const Store2 = ({ publicMode = false }) => {
  const navigate = useNavigate();
  const [storeData, setStoreData] = useState(null);
  const [courses, setCourses] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [coursePricingTiers, setCoursePricingTiers] = useState([]);
  const [purchasing, setPurchasing] = useState(false);
  const [userHasCourse, setUserHasCourse] = useState(false);
  const [tiersExpanded, setTiersExpanded] = useState(false);
  const [showSubjectsPopup, setShowSubjectsPopup] = useState(false);
  const [popupCourse, setPopupCourse] = useState(null);
  const [expandedSubjects, setExpandedSubjects] = useState(new Set());
  const [bundleSubjects, setBundleSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidation, setCouponValidation] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedIsBundle, setSelectedIsBundle] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('coupon');
  const [accessCode, setAccessCode] = useState('');
  const [cliqConfig, setCliqConfig] = useState(null);
  const [cliqConfigLoading, setCliqConfigLoading] = useState(false);
  const [senderReference, setSenderReference] = useState('');
  const [aliasCopied, setAliasCopied] = useState(false);
  const [cliqSuccessModal, setCliqSuccessModal] = useState(null);
  
  // DLR Extras state
  const [dlrExtras, setDlrExtras] = useState({
    MIC: false,
    SKT: false,
    MEK: false,
    RMS: false,
    ROT: false,
    OWT: false,
    PPT: false
  });
  
  // Currency state
  const [currency, setCurrency] = useState('JOD');
  const currencyRates = CURRENCY_RATES;

  useEffect(() => {
    fetchStoreData();
  }, []);

  useEffect(() => {
    if (showPaymentModal && paymentMethod === 'cliq') {
      fetchCliqConfig();
    }
  }, [showPaymentModal, paymentMethod]);

  const fetchCliqConfig = async () => {
    try {
      setCliqConfigLoading(true);
      const response = await fetch(`${API_URL}/api/payments/cliq/config/`);
      if (response.ok) {
        const data = await response.json();
        setCliqConfig(data);
      } else {
        setCliqConfig(null);
      }
    } catch (error) {
      setCliqConfig(null);
    } finally {
      setCliqConfigLoading(false);
    }
  };

  const fetchStoreData = async () => {
    try {
      const headers = {
        'Content-Type': 'application/json',
      };
      if (!publicMode) {
        headers['Authorization'] = `Bearer ${localStorage.getItem('atpl_access_token')}`;
      }
      const response = await fetch(`${API_URL}/api/payments/store/`, {
        headers,
      });
      
      if (response.ok) {
        const data = await response.json();
                
        // Store API now returns optimized course data with pricing tiers
        const coursesData = data.courses || [];
        
        setCourses(coursesData);
        setBundles(data.bundles || []);
        setStoreData(data);
        
        // Auto-select first course if available
        if (coursesData && coursesData.length > 0) {
          setSelectedCourse(coursesData[0]);
          setCoursePricingTiers(coursesData[0].pricing_tiers || []);
          setUserHasCourse(data.user_purchases?.some(purchase => 
            purchase.pricing_tier?.course?.id === coursesData[0].id && purchase.is_active
          ) || false);
        }
      }
    } catch (error) {
      // Error fetching store data
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseSubjects = async (courseId) => {
    try {
      const headers = {};
      if (!publicMode) {
        headers['Authorization'] = `Bearer ${localStorage.getItem('atpl_access_token')}`;
      }
      const response = await fetch(`${API_URL}/api/courses/${courseId}/subjects/`, {
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.results || [];
      }
    } catch (error) {
      // Error fetching course subjects
    }
    return [];
  };

  // There is no bundle-subjects endpoint; the bundle covers every course in the store,
  // so combine their (public) subject lists.
  const fetchBundleSubjects = async () => {
    const lists = await Promise.all(courses.map((c) => fetchCourseSubjects(c.id)));
    const subjects = lists.flat();
    setBundleSubjects(subjects);
    return subjects;
  };

  const validateCoupon = async (pricingTierId) => {
    if (!couponCode.trim()) {
      setCouponValidation(null);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/payments/coupons/validate/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        },
        body: JSON.stringify({
          code: couponCode,
          pricing_tier_id: pricingTierId
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCouponValidation(data);
      }
    } catch (error) {
      // Error validating coupon
    }
  };

  // Calculate DLR extras price
  const calculateExtrasPrice = () => {
    return Object.entries(DLR_EXTRA_PRICES)
      .reduce((total, [game, price]) => total + (dlrExtras[game] ? price : 0), 0);
  };

  // Format price with selected currency
  const formatPrice = (priceUSD) => convertPrice(priceUSD, currency);

  // Check if current course is DLR
  const isDLRCourse = selectedCourse?.code === 'DLR';
  // DLR game extras only apply to single-course DLR purchases, never to bundles
  const extrasApply = isDLRCourse && !selectedIsBundle;

  const openPaymentModal = (tier, isBundle = false) => {
    if (publicMode) {
      navigate('/login');
      return;
    }
    setSelectedTier(tier);
    setSelectedIsBundle(isBundle);
    setAccessCode('');
    setPaymentMethod('cliq');
    setCliqConfig(null);
    setShowPaymentModal(true);
  };

  const handlePurchaseWithCode = async () => {
    if (publicMode) {
      navigate('/login');
      return;
    }
    if (!selectedTier || purchasing) return;
    
    if (!accessCode.trim()) {
      alert('Please enter an access code or coupon');
      return;
    }
    
    setPurchasing(true);
    try {
      // Redeem as a purchase coupon
      const response = await fetch(`${API_URL}/api/payments/purchase-coupons/redeem/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        },
        body: JSON.stringify({
          code: accessCode.trim(),
          pricing_tier_id: selectedTier.id,
          game_extras: extrasApply ? dlrExtras : {}
        })
      });

      if (response.ok) {
        const result = await response.json();
        const courseName = result.purchase?.course || 'the course';
        const durationDays = result.purchase?.duration_days || 'unlimited';
        const gameAccessMsg = result.purchase?.game_accesses?.length > 0 
          ? ` Game access granted: ${result.purchase.game_accesses.join(', ')}.`
          : '';
        alert(`Purchase successful! You now have access to ${courseName} for ${durationDays} days.${gameAccessMsg}`);
        setShowPaymentModal(false);
        window.location.reload();
      } else {
        const error = await response.json();
        alert(`Coupon error: ${error.error || error.detail || 'Invalid coupon or mismatch with selected options'}`);
      }
    } catch (error) {
      alert('Purchase failed. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const handleCliqConfirm = async () => {
    if (publicMode) {
      navigate('/login');
      return;
    }
    if (!selectedTier || purchasing) return;

    setPurchasing(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/cliq/request/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        },
        body: JSON.stringify({
          ...(selectedIsBundle
            ? { bundle_pricing_tier_id: selectedTier.id }
            : { pricing_tier_id: selectedTier.id, game_extras: extrasApply ? dlrExtras : {} }),
          sender_reference: senderReference.trim()
        })
      });

      if (response.ok) {
        const result = await response.json();
        setShowPaymentModal(false);
        setAccessCode('');
        setPaymentMethod('coupon');
        setCliqConfig(null);
        setSenderReference('');
        setCliqSuccessModal(result);
      } else {
        const error = await response.json();
        alert(`Cliq request failed: ${error.error || error.detail || 'Please try again'}`);
      }
    } catch (error) {
      alert('Cliq request failed. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setAccessCode('');
    setPaymentMethod('coupon');
    setCliqConfig(null);
  };

  if (loading) {
    return (
      <AppPage>
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
        </div>
      </AppPage>
    );
  }

  const { user_purchases } = storeData || {};
  const bundle = bundles[0];
  const hasUserCourse = user_purchases?.some((purchase) =>
    purchase.pricing_tier?.course?.id === selectedCourse?.id && purchase.is_active
  );
  const isBundleOwned = user_purchases?.some((purchase) =>
    purchase.pricing_tier?.bundle && purchase.pricing_tier.bundle.id === bundle?.id && purchase.is_active
  );
  const extrasUSD = isDLRCourse ? calculateExtrasPrice() : 0;
  const selectedGames = DLR_GAMES.filter((g) => dlrExtras[g.code]);
  const perMonthUSD = (t) => parseFloat(t.price) / (t.duration_days / 30);
  const bestValueId = (tiers) => (tiers.length > 1
    ? tiers.reduce((best, t) => (perMonthUSD(t) < perMonthUSD(best) ? t : best), tiers[0]).id : null);
  const byDuration = (tiers = []) => [...tiers].sort((a, b) => a.duration_days - b.duration_days);
  const modalExtrasUSD = extrasApply ? calculateExtrasPrice() : 0;
  const modalTotalUSD = selectedTier ? parseFloat(selectedTier.price) + modalExtrasUSD : 0;

  const selectCourse = (course) => {
    setSelectedCourse(course);
    setCoursePricingTiers(course.pricing_tiers || []);
    setUserHasCourse(user_purchases?.some((purchase) =>
      purchase.pricing_tier?.course?.id === course.id && purchase.is_active
    ) || false);
  };

  // What the hero shows for the selected programme (the bundle when no course is selected)
  const hero = selectedCourse
    ? {
      name: selectedCourse.name,
      image: getImageUrl(selectedCourse.banner_image),
      blurb: PROGRAMME_BLURBS[selectedCourse.code] || selectedCourse.description,
      stats: [
        { value: selectedCourse.subject_count || 0, label: 'subjects' },
        { value: Number(selectedCourse.total_questions || 0).toLocaleString(), label: 'questions' },
        ...(isDLRCourse ? [{ value: DLR_GAMES.length, label: 'screening games' }] : []),
      ],
      viewSubjects: async () => {
        const subjects = await fetchCourseSubjects(selectedCourse.id);
        setPopupCourse({ name: selectedCourse.name, subjects });
        setShowSubjectsPopup(true);
      },
    }
    : {
      name: bundle?.name || 'All-in-One',
      image: getImageUrl(bundle?.banner_image),
      blurb: PROGRAMME_BLURBS.bundle,
      stats: [
        { value: bundle?.total_subjects || 0, label: 'subjects' },
        { value: Number(bundle?.total_questions || 0).toLocaleString(), label: 'questions' },
      ],
      viewSubjects: async () => {
        const subjects = await fetchBundleSubjects();
        setPopupCourse({ name: bundle?.name || 'All-in-One', subjects });
        setShowSubjectsPopup(true);
      },
    };

  // One plan card; course plans include the selected DLR games in every price shown
  const renderPlanCard = (tier, { isBundle = false, owned = false, bestId = null }) => {
    const extras = isBundle ? 0 : extrasUSD;
    const discountPercentage = tier.discount_percentage || 0;
    const listPrice = discountPercentage > 0 ? parseFloat(tier.price) / (1 - discountPercentage / 100) : null;
    const total = parseFloat(tier.price) + extras;
    const highlighted = tier.is_popular || tier.id === bestId;
    return (
      <div key={tier.id} className={`flex flex-col p-5 transition-colors ${highlighted ? CARD_FEATURED : `${CARD} hover:border-slate-300 dark:hover:border-white/25`}`}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">{tier.name}</h3>
            <p className="text-xs text-slate-500 dark:text-gray-400">{tier.duration_days} days of access</p>
          </div>
          <div className="flex gap-1.5">
            {discountPercentage > 0 && <span className="rounded-full bg-rose-500 px-2.5 py-0.5 text-xs font-semibold text-white">-{discountPercentage}%</span>}
            {highlighted && <span className="whitespace-nowrap rounded-full bg-primary-600 px-2.5 py-0.5 text-xs font-semibold text-white">{tier.is_popular ? 'Popular' : 'Best value'}</span>}
          </div>
        </div>
        <div className="mt-4 flex items-baseline gap-2">
          <span className="text-3xl font-semibold tracking-tight text-slate-900 tabular-nums dark:text-white">{formatPrice(total)}</span>
          {listPrice && <span className="text-sm text-slate-400 line-through">{formatPrice(listPrice + extras)}</span>}
        </div>
        <p className="mt-1 min-h-[1.25rem] text-sm text-slate-500 dark:text-gray-400">
          {extras > 0
            ? `Plan ${formatPrice(tier.price)} + games ${formatPrice(extras)}`
            : tier.duration_days >= 28 ? `${formatPrice(perMonthUSD(tier))} per month` : 'Short-term access'}
        </p>
        <div className="mt-auto pt-5">
          {owned ? (
            <div className="rounded-lg bg-emerald-500/10 py-2.5 text-center text-sm font-semibold text-emerald-700 dark:text-emerald-300">Owned</div>
          ) : (
            <button
              onClick={() => openPaymentModal(tier, isBundle)}
              disabled={purchasing}
              className={`w-full ${highlighted ? BTN_PRIMARY : BTN_SECONDARY}`}
            >
              Buy now
            </button>
          )}
        </div>
      </div>
    );
  };

  const tiers = selectedCourse ? coursePricingTiers : (bundle?.pricing_tiers || []);
  const owned = selectedCourse ? hasUserCourse : isBundleOwned;

  return (
    <AppPage className={publicMode ? 'm-0 sm:m-0 min-h-screen' : ''}>
      {publicMode && <div className="-mx-3 -mt-5 mb-6 sm:-mx-6 sm:-mt-8"><MarketingNav /></div>}

      {/* Programme banner: pick what to buy */}
      <HeroPanel image={hero.image || `${API_URL}/media/banners/banner.jpeg`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div role="group" aria-label="Programme" className="inline-flex max-w-full overflow-x-auto rounded-xl border border-white/15 bg-black/30 p-1 backdrop-blur">
            {courses.map((course) => (
              <button
                key={course.id}
                onClick={() => selectCourse(course)}
                aria-current={selectedCourse?.id === course.id}
                className={`flex-shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors sm:px-4 sm:text-sm ${selectedCourse?.id === course.id ? 'bg-white text-slate-900' : 'text-gray-300 hover:text-white'}`}
              >
                <span className="sm:hidden">{course.code || course.name}</span>
                <span className="hidden sm:inline">{course.name}</span>
              </button>
            ))}
            {bundle && (
              <button
                onClick={() => setSelectedCourse(null)}
                aria-current={!selectedCourse}
                className={`flex-shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors sm:px-4 sm:text-sm ${!selectedCourse ? 'bg-white text-slate-900' : 'text-gray-300 hover:text-white'}`}
              >
                All in One
              </button>
            )}
          </div>
          {!publicMode && (
            <Link to="/support" className="inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-white">
              <QuestionMarkCircleIcon className="h-4 w-4" /> Need help?
            </Link>
          )}
        </div>

        <h1 className="mt-6 max-w-2xl text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl">{hero.name}</h1>
        {hero.blurb && <p className="mt-2 hidden max-w-2xl text-gray-300 sm:block">{hero.blurb}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-400">
          {hero.stats.map((s) => (
            <span key={s.label}><span className="mr-1 font-semibold text-white tabular-nums">{s.value}</span>{s.label}</span>
          ))}
          <button onClick={hero.viewSubjects} className="inline-flex items-center gap-1 font-medium text-primary-300 hover:text-primary-200">
            View subjects <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
      </HeroPanel>

      {owned && (
        <div className="mt-6 flex items-center gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-3 text-sm text-emerald-800 dark:text-emerald-200">
          <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-emerald-500" />
          You already have access to {selectedCourse ? 'this programme' : 'the All-in-One plan'}.
        </div>
      )}

      {/* Plans */}
      <section className="mt-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">Choose your plan</h2>
          <Segmented
            label="Currency"
            value={currency}
            onChange={setCurrency}
            options={Object.keys(currencyRates).map((c) => ({ value: c, label: c }))}
          />
        </div>

        {/* DLR screening games: added to every plan price below */}
        {isDLRCourse && (
          <div className={`mb-4 p-4 ${CARD}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-900 dark:text-white">
                Add screening games <span className="font-normal text-slate-500 dark:text-gray-400">(optional)</span>
              </p>
              {selectedGames.length > 0 && (
                <p className="flex items-center gap-3 text-sm">
                  <span className="font-medium text-primary-600 dark:text-primary-300">{selectedGames.length} selected · +{formatPrice(extrasUSD)} on every plan</span>
                  <button
                    onClick={() => setDlrExtras((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, false])))}
                    className="text-slate-500 hover:underline dark:text-gray-400"
                  >
                    Clear
                  </button>
                </p>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {DLR_GAMES.map((g) => {
                const on = !!dlrExtras[g.code];
                return (
                  <button
                    key={g.code}
                    onClick={() => setDlrExtras((prev) => ({ ...prev, [g.code]: !prev[g.code] }))}
                    aria-pressed={on}
                    title={g.name}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${on
                      ? 'border-primary-600 bg-primary-600 text-white'
                      : 'border-slate-200 text-slate-700 hover:border-primary-400 dark:border-white/15 dark:text-gray-200 dark:hover:border-primary-500/60'}`}
                  >
                    {on && <CheckIcon className="h-4 w-4" />}
                    <span className="font-semibold">{g.code}</span>
                    <span className={on ? 'text-white/80' : 'text-slate-500 dark:text-gray-400'}>+{formatPrice(g.price)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className={`grid gap-4 sm:grid-cols-2 ${tiers.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
          {byDuration(tiers).map((tier) =>
            renderPlanCard(tier, { isBundle: !selectedCourse, owned, bestId: bestValueId(tiers) }))}
        </div>
        <p className="mt-4 text-center text-sm text-slate-500 dark:text-gray-400">
          One-time payment, no auto-renewal. Pay by CliQ in JOD or with an access code; access starts once your transfer is confirmed (usually within 12 hours).
        </p>
      </section>

      {/* Purchases */}
      {user_purchases?.length > 0 && (
        <section className="mt-12">
          <SectionTitle eyebrow="Your account" title="My purchases" />
          <ul className={`divide-y divide-slate-200 overflow-hidden dark:divide-white/10 ${CARD}`}>
            {user_purchases.map((purchase) => (
              <li key={purchase.id} className="flex items-center justify-between gap-4 px-5 py-4 text-sm">
                <span className="min-w-0">
                  <span className="block truncate font-medium text-slate-900 dark:text-white">
                    {purchase.pricing_tier?.course?.name || purchase.pricing_tier?.bundle?.name} · {purchase.pricing_tier?.name}
                  </span>
                  {purchase.access_expires_at && (
                    <span className="text-xs text-slate-500 dark:text-gray-400">
                      {purchase.is_active ? 'Until' : 'Ended'} {new Date(purchase.access_expires_at).toLocaleDateString()}
                    </span>
                  )}
                </span>
                <span className="flex flex-shrink-0 items-center gap-3">
                  <span className="text-slate-600 tabular-nums dark:text-gray-300">{formatAmount(purchase.amount_paid, purchase.currency)}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${purchase.is_active
                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                    : 'bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-gray-400'}`}
                  >
                    {purchase.is_active ? 'Active' : 'Expired'}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedTier && (
        <Modal onClose={closePaymentModal} className="max-w-md">
          <div className="flex items-start justify-between border-b border-slate-200 p-6 dark:border-white/10">
            <div>
              <Eyebrow>Checkout</Eyebrow>
              <h3 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">Complete purchase</h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
                {selectedIsBundle ? (bundle?.name || 'All-in-One') : selectedCourse?.name} · {selectedTier.name}
              </p>
            </div>
            <CloseButton onClick={closePaymentModal} />
          </div>

          <div className="flex-1 space-y-6 overflow-y-auto p-6">
            {/* Payment method (coupons redeem single-course tiers only) */}
            <div>
              <p className="mb-2 text-sm font-medium text-slate-700 dark:text-gray-300">Payment method</p>
              <div className={`grid gap-2 ${selectedIsBundle ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {[
                  { value: 'cliq', icon: BanknotesIcon, title: 'CliQ', sub: 'Bank transfer' },
                  ...(!selectedIsBundle ? [{ value: 'coupon', icon: TicketIcon, title: 'Access code', sub: 'Use coupon' }] : []),
                ].map((m) => (
                  <button
                    key={m.value}
                    onClick={() => setPaymentMethod(m.value)}
                    aria-pressed={paymentMethod === m.value}
                    className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${paymentMethod === m.value
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-600/15'
                      : 'border-slate-200 hover:border-slate-300 dark:border-white/10 dark:hover:border-white/25'}`}
                  >
                    <m.icon className="h-6 w-6 flex-shrink-0 text-primary-600 dark:text-primary-300" />
                    <span>
                      <span className="block text-sm font-semibold text-slate-900 dark:text-white">{m.title}</span>
                      <span className="block text-xs text-slate-500 dark:text-gray-400">{m.sub}</span>
                    </span>
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-400 dark:text-gray-500">Card, Apple Pay and Google Pay are coming soon.</p>
            </div>

            {paymentMethod === 'coupon' && (
              <div>
                <label htmlFor="access-code" className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">Access code</label>
                <input
                  id="access-code"
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="Enter your access code"
                  className={INPUT}
                />
                <p className="mt-2 text-xs text-slate-500 dark:text-gray-400">Access codes are provided by authorised distributors.</p>
              </div>
            )}

            {paymentMethod === 'cliq' && (
              <div className="rounded-xl border border-slate-200 p-4 dark:border-white/10">
                <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
                  <BanknotesIcon className="h-4 w-4 text-primary-600 dark:text-primary-300" />
                  Pay with CliQ
                </h4>
                <div className="mt-3 rounded-lg bg-slate-50 p-3 dark:bg-white/[0.04]">
                  <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-gray-400">Total amount to wire</p>
                  {/* CliQ is always billed in JOD, whatever display currency is selected */}
                  <p className="mt-0.5 text-2xl font-semibold tracking-tight text-slate-900 tabular-nums dark:text-white">{convertPrice(modalTotalUSD, 'JOD')}</p>
                </div>
                <ol className="mt-4 list-inside list-decimal space-y-2 text-sm text-slate-600 dark:text-gray-300">
                  <li>
                    Transfer the exact amount to CliQ alias{' '}
                    <span className="font-mono font-semibold text-slate-900 dark:text-white">
                      {cliqConfigLoading ? 'Loading...' : (cliqConfig?.alias || 'Not configured')}
                    </span>
                    {cliqConfig?.alias && (
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard?.writeText(cliqConfig.alias)
                            .then(() => { setAliasCopied(true); setTimeout(() => setAliasCopied(false), 2000); })
                            .catch(() => {});
                        }}
                        className="ml-2 inline-flex items-center rounded-md border border-slate-300 px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10"
                      >
                        {aliasCopied ? 'Copied' : 'Copy'}
                      </button>
                    )}
                  </li>
                  <li>
                    Tell us who sent it, so we can match your transfer faster (optional):
                    <input
                      type="text"
                      value={senderReference}
                      onChange={(e) => setSenderReference(e.target.value)}
                      maxLength={120}
                      placeholder="Sender name or CliQ reference"
                      className={`mt-2 ${INPUT}`}
                    />
                  </li>
                  <li>Press <strong className="text-slate-900 dark:text-white">Confirm Transfer</strong> below.</li>
                  <li>We review transfers within 12 hours and then unlock your access.</li>
                </ol>
                {!cliqConfigLoading && !cliqConfig?.alias && (
                  <p className="mt-3 text-xs text-rose-600 dark:text-rose-400">CliQ payment is not configured yet. Please contact support.</p>
                )}
              </div>
            )}

            {/* Summary */}
            <dl className="space-y-2 rounded-xl bg-slate-50 p-4 text-sm dark:bg-white/[0.04]">
              <div className="flex justify-between"><dt className="text-slate-500 dark:text-gray-400">Plan</dt><dd className="font-medium text-slate-900 dark:text-white">{selectedTier.name} · {selectedTier.duration_days} days</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500 dark:text-gray-400">Base price</dt><dd className="font-medium text-slate-900 tabular-nums dark:text-white">{formatPrice(selectedTier.price)}</dd></div>
              {modalExtrasUSD > 0 && selectedGames.map((g) => (
                <div key={g.code} className="flex justify-between">
                  <dt className="text-slate-500 dark:text-gray-400">{g.code} · {g.name}</dt>
                  <dd className="text-slate-900 tabular-nums dark:text-white">+{formatPrice(g.price)}</dd>
                </div>
              ))}
              <div className="flex justify-between border-t border-slate-200 pt-2 dark:border-white/10">
                <dt className="font-medium text-slate-900 dark:text-white">Total</dt>
                <dd className="text-lg font-semibold text-slate-900 tabular-nums dark:text-white">{formatPrice(modalTotalUSD)}</dd>
              </div>
            </dl>
          </div>

          <div className="flex gap-3 border-t border-slate-200 p-6 dark:border-white/10">
            <button onClick={closePaymentModal} className={`flex-1 ${BTN_SECONDARY}`}>Cancel</button>
            <button
              onClick={() => (paymentMethod === 'cliq' ? handleCliqConfirm() : handlePurchaseWithCode())}
              disabled={
                purchasing
                || (paymentMethod === 'coupon' && !accessCode.trim())
                || (paymentMethod === 'cliq' && (!cliqConfig?.alias || cliqConfigLoading))
              }
              className={`flex-1 ${BTN_PRIMARY}`}
            >
              {purchasing ? 'Processing...' : paymentMethod === 'cliq' ? 'Confirm Transfer' : 'Activate Access'}
            </button>
          </div>
        </Modal>
      )}

      {/* CliQ Success Popup */}
      {cliqSuccessModal && (
        <Modal onClose={() => setCliqSuccessModal(null)} className="max-w-md">
          <div className="p-6 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15">
              <CheckCircleIcon className="h-7 w-7 text-emerald-500" />
            </span>
            <h3 className="mt-4 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">Transfer Request Submitted</h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">{cliqSuccessModal.product_name}</p>
            <p className="mt-4 text-sm text-slate-600 dark:text-gray-300">We review transfers within 12 hours and then unlock your access.</p>
          </div>
          <dl className="mx-6 grid grid-cols-2 gap-4 rounded-xl bg-slate-50 p-4 text-sm dark:bg-white/[0.04]">
            {[
              ['Amount to wire', `${cliqSuccessModal.amount} ${cliqSuccessModal.currency}`],
              ['CliQ alias', cliqSuccessModal.alias],
              ['Status', cliqSuccessModal.status],
              ['Submitted', new Date(cliqSuccessModal.created_at).toLocaleString()],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="text-xs uppercase tracking-wider text-slate-500 dark:text-gray-400">{k}</dt>
                <dd className="mt-0.5 font-semibold capitalize text-slate-900 dark:text-white">{v}</dd>
              </div>
            ))}
          </dl>
          <div className="flex justify-end gap-3 p-6">
            <button onClick={() => setCliqSuccessModal(null)} className={BTN_SECONDARY}>Close</button>
            <button onClick={() => window.location.reload()} className={BTN_PRIMARY}>Done</button>
          </div>
        </Modal>
      )}

      {/* Subjects Popup Modal */}
      {showSubjectsPopup && popupCourse && (
        <Modal onClose={() => { setShowSubjectsPopup(false); setExpandedSubjects(new Set()); }} className="max-w-3xl">
          <div className="flex items-start justify-between border-b border-slate-200 p-6 dark:border-white/10">
            <div>
              <Eyebrow>What&apos;s included</Eyebrow>
              <h3 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">{popupCourse.name}</h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">{popupCourse.subjects?.length || 0} subjects available</p>
            </div>
            <CloseButton onClick={() => { setShowSubjectsPopup(false); setExpandedSubjects(new Set()); }} />
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {popupCourse.subjects?.length > 0 ? (
              <ul className="space-y-1">
                {popupCourse.subjects.map((subject, index) => {
                  const key = subject.id || index;
                  const isExpanded = expandedSubjects.has(key);
                  const totalQuestions = subject.chapters?.reduce((sum, chapter) => sum + (chapter.question_count || 0), 0) || 0;
                  return (
                    <li key={key}>
                      <button
                        onClick={() => {
                          const next = new Set(expandedSubjects);
                          if (isExpanded) next.delete(key); else next.add(key);
                          setExpandedSubjects(next);
                        }}
                        aria-expanded={isExpanded}
                        className="flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.04]"
                      >
                        <span className="relative flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary-600/10 text-primary-600 dark:text-primary-300">
                          <BookOpenIcon className="h-4 w-4" />
                          {subject.image && (
                            <img src={getImageUrl(subject.image)} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-slate-900 dark:text-white">
                            {subject.code && <span className="mr-1.5 text-xs font-semibold text-primary-600 dark:text-primary-300">{subject.code}</span>}
                            {subject.name}
                          </span>
                          <span className="block text-xs text-slate-500 dark:text-gray-400">{totalQuestions.toLocaleString()} questions</span>
                        </span>
                        {subject.chapters?.length > 0 && (
                          <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        )}
                      </button>
                      {isExpanded && subject.chapters?.length > 0 && (
                        <ul className="mb-2 ml-14 mr-2 space-y-1">
                          {subject.chapters.map((chapter, chapterIndex) => (
                            <li key={chapterIndex} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-xs dark:bg-white/[0.04]">
                              <span className="text-slate-700 dark:text-gray-300">{chapter.name}</span>
                              <span className="text-slate-500 tabular-nums dark:text-gray-400">{chapter.question_count || 0}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="py-12 text-center text-slate-500 dark:text-gray-400">
                <BookOpenIcon className="mx-auto mb-4 h-12 w-12 opacity-30" />
                <p className="font-medium">No subjects available</p>
                <p className="mt-1 text-sm">This course doesn&apos;t have any subjects configured yet.</p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </AppPage>
  );
};

const INPUT = 'block w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-white/15 dark:bg-white/[0.04] dark:text-white dark:placeholder-gray-500';

const Modal = ({ children, onClose, className = '' }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" onClick={onClose}>
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.stopPropagation()}
      className={`flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-900 ${className}`}
    >
      {children}
    </div>
  </div>
);

const CloseButton = ({ onClick }) => (
  <button onClick={onClick} aria-label="Close" className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10 dark:hover:text-white">
    <XMarkIcon className="h-5 w-5" />
  </button>
);

export default Store2;
